import { useEffect, useRef } from 'react';
import { useLocale } from '@/context/LocaleContext';

interface BattlepassExpModalProps {
  open: boolean;
  onClose: () => void;
  className?: string;
}

const EXP_LINES_COL1 = [
  { icon: 'ph-buildings', key: 'bp_exp_tower_defense_victory' },
  { icon: 'ph-key', key: 'bp_exp_hell_prison_victory' },
  { icon: 'ph-magic-wand', key: 'bp_exp_witch_hunt_victory' },
  { icon: 'ph-sword', key: 'bp_exp_last_man_standing' },
  { icon: 'ph-flower', key: 'bp_exp_garden_job_victory' },
  { icon: 'ph-flag', key: 'bp_exp_capture_flag_victory' },
  { icon: 'ph-shield', key: 'bp_exp_battle_arena_victory' },
  { icon: 'ph-medal', key: 'bp_exp_survival' },
  { icon: 'ph-fire', key: 'bp_exp_madness' },
] as const;

const EXP_LINES_COL2 = [
  { icon: 'ph-trophy', key: 'bp_exp_league_map_victory' },
  { icon: 'ph-crosshair', key: 'bp_exp_medusa_victory' },
  { icon: 'ph-bird', key: 'bp_exp_roc_victory' },
  { icon: 'ph-star', key: 'bp_exp_normal_uniques_victory' },
  { icon: 'ph-star', key: 'bp_exp_arabian_uniques_victory' },
  { icon: 'ph-target', key: 'bp_exp_job_kills' },
  { icon: 'ph-package', key: 'bp_exp_job_delivery' },
  { icon: 'ph-star', key: 'bp_exp_legendary_uniques_victory' },
  { icon: 'ph-chat-circle', key: 'bp_exp_global_chat' },
] as const;

export function BattlepassExpModal({ open, onClose, className }: BattlepassExpModalProps) {
  const { t } = useLocale();
  const modalRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (!open) return;

    const modal = modalRef.current;
    if (!modal) return;

    modal.classList.remove('battlepass-exp-modal-open');
    const frame = requestAnimationFrame(() => {
      if (openRef.current) {
        modal.classList.add('battlepass-exp-modal-open');
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      id="battlepass-exp-modal"
      ref={modalRef}
      className={['battlepass-exp-modal', className].filter(Boolean).join(' ')}
      style={{ display: 'flex' }}
    >
      <div
        className="battlepass-exp-modal-overlay"
        onClick={onClose}
        onKeyDown={() => {}}
        role="presentation"
      />
      <div className="battlepass-exp-modal-content">
        <div className="battlepass-exp-modal-header">
          <h2 className="battlepass-exp-modal-title">
            <i className="ph-light ph-info-circle" aria-hidden />
            {t('bp_exp_info_title')}
          </h2>
          <button type="button" className="battlepass-exp-modal-close" onClick={onClose}>
            <i className="ph-light ph-x" aria-hidden />
          </button>
        </div>
        <div className="battlepass-exp-modal-body">
          <div className="battlepass-exp-grid-lines">
            <div className="battlepass-exp-column">
              {EXP_LINES_COL1.map(({ icon, key }) => (
                <div className="battlepass-exp-line" key={key}>
                  <i className={`battlepass-exp-icon ph-light ${icon}`} aria-hidden />
                  <span className="battlepass-exp-label">{t(key)}</span>
                </div>
              ))}
            </div>
            <div className="battlepass-exp-column">
              {EXP_LINES_COL2.map(({ icon, key }) => (
                <div className="battlepass-exp-line" key={key}>
                  <i className={`battlepass-exp-icon ph-light ${icon}`} aria-hidden />
                  <span className="battlepass-exp-label">{t(key)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
